/**
 * Records API Schemas
 *
 * Pure Zod schemas for API request/response validation.
 * DB types come from @aica/database repositories.
 *
 * SINGLE SOURCE OF TRUTH for API contract.
 */

import { z } from 'zod/v4';
import { callSeriesOutcomeSemanticSchema } from './call-series-outcomes';
import {
  projectIdsFilterSchema,
  searchTotalIsPartialSchema,
  searchCursorParamSchema,
  searchCursorResponseSchema,
  searchFallbackSchema,
  searchTruncatedToSchema,
  RECORD_SEARCH_MAX_LENGTH,
  dashboardFlowDirectionSchema,
} from './common';
import { crmStageProgressionSchema } from './stage-progression';
import { REPORT_FUNNEL_BUCKETS } from './reports';
import { analysisPublicationSchema, replayModeSchema } from './pipelines';
import { sourceRoleSchema } from './sources';
import {
  criterionFilterOptionSchema,
  recordCriteriaFilterSchema,
} from './criteria-filter';
import {
  recordScriptStepFilterEchoSchema,
  recordScriptStepQuerySchema,
} from './script-step-filter';

// ============================================================================
// Enums
// ============================================================================

/**
 * Type of communication record
 *
 * Defines the channel through which the interaction occurred:
 * - `call`: Voice call (phone/VoIP) - requires audio transcription
 * - `chat`: Text-based chat conversation - uses direct transcript
 * - `email`: Email exchange - uses email body as transcript
 */
export const recordTypeSchema = z.enum(['call', 'chat', 'email']);

/**
 * CRM entity type that a record is associated with
 *
 * Links records to CRM objects for context and tracking:
 * - `deal`: Sales opportunity or deal
 * - `lead`: Potential customer/prospect
 * - `contact`: Individual person in CRM
 * - `company`: Organization/account in CRM
 */
export const crmEntityTypeSchema = z.enum(['deal', 'lead', 'contact', 'company']);

/**
 * CRM stage semantic - coarse-grained bucket the raw Bitrix24 STAGE_ID maps to.
 *
 * Used for stage-aware scoring (Phase B): analysis criteria can vary by where
 * the deal sits in the pipeline, without depending on org-specific STAGE_ID
 * strings. `other` covers stages that don't map cleanly (e.g. custom/rare
 * pipeline stages).
 */
export const crmStageSemanticSchema = z.enum([
  'discovery',
  'qualification',
  'proposal',
  'negotiation',
  'closing',
  'post_sale',
  'other',
]);

/** All 7 semantic phases, in pipeline order — drives the semantic-defaults
 * authoring UI (Phase B, B-7). Mirrors the PROFILE_BINDINGS/SCRIPT_BINDINGS
 * `.options` convention. */
export const CRM_STAGE_SEMANTICS = crmStageSemanticSchema.options;

/**
 * CRM stage source - how the stage snapshot on a record was captured.
 *
 * - `live`: Read directly from the CRM entity at call time
 * - `stagehistory`: Reconstructed from Bitrix24 stage-history log (backfill)
 * - `unknown`: Capture attempted but the source could not be determined
 */
export const crmStageSourceSchema = z.enum(['live', 'stagehistory', 'unknown']);

/**
 * Where a record's `crm_stage_semantic` came from (#1206) — not how the stage
 * was captured (that is `crmStageSourceSchema`).
 *
 * - `crm`: the CRM's own marker — Bitrix24 says the stage is won or lost
 * - `customer`: the customer mapped the stage to a phase themselves
 * - `name`: guessed by AI from the stage names — shown as a guess
 */
export const crmStageSemanticOriginSchema = z.enum(['crm', 'customer', 'name']);

/**
 * Call category - automatic categorization of the call/chat purpose
 *
 * Special categories (set automatically, skip pipeline):
 * - `missed`: Call was not answered (zero duration, MISSED_CALL flag, or RESULT_STREAM=4)
 * - `short`: Call was answered but too brief for analysis (1–15 sec)
 *
 * 10 standard categories (Dialpad-style, set by LLM analysis):
 * - `product_inquiry`: Questions about products/services
 * - `purchase`: Buying, ordering, payment processing
 * - `support`: Technical support, troubleshooting
 * - `billing`: Invoices, charges, payment issues
 * - `complaint`: Dissatisfaction, escalation requests
 * - `follow_up`: Returning to previous conversation/issue
 * - `scheduling`: Appointments, meetings, demos
 * - `cancellation`: Cancel subscription/order/service
 * - `onboarding`: New customer setup, training
 * - `other`: Doesn't fit any specific category
 */
/**
 * Categories produced by the LLM categorizer and accepted by analysis profile
 * bindings. `missed` and `short` are derived intake outcomes, not model
 * classifications, so they deliberately live outside this tuple.
 */
export const ANALYZED_CALL_CATEGORIES = [
  'product_inquiry',
  'purchase',
  'scheduling',
  'support',
  'billing',
  'onboarding',
  'complaint',
  'cancellation',
  'follow_up',
  'other',
] as const;

export const analyzedCallCategorySchema = z.enum(ANALYZED_CALL_CATEGORIES);
export type AnalyzedCallCategory = z.infer<typeof analyzedCallCategorySchema>;

/**
 * Intake outcomes under which a record never gets a transcript worth asking
 * about: a missed call, a call below the organization's minimum duration.
 * One list for every side that asks «can this record answer anything» — the
 * record contour (`neverTranscribed`) and the whole-base candidate predicate
 * in `@aica/database` (#765 Ф3, PR 7b).
 */
export const NO_TRANSCRIPT_CALL_CATEGORIES = ['missed', 'short'] as const;

const NO_TRANSCRIPT_CATEGORIES: ReadonlySet<string> = new Set(
  NO_TRANSCRIPT_CALL_CATEGORIES
);

/**
 * A short or missed call: no transcript worth asking about will ever come.
 * Here, not in workflows-api, because edge-api asks it too — before the paid
 * pass over a question about such a record (#2449).
 */
export function neverTranscribed(record: {
  isShortCall: boolean | null;
  callCategory: string | null;
}): boolean {
  return (
    record.isShortCall === true ||
    (record.callCategory !== null && NO_TRANSCRIPT_CATEGORIES.has(record.callCategory))
  );
}

export const callCategorySchema = z.enum([
  ...NO_TRANSCRIPT_CALL_CATEGORIES,
  ...ANALYZED_CALL_CATEGORIES,
]);

/**
 * Categories where the customer is expressing an intent to buy — a subset of
 * `callCategorySchema`'s values. `satisfies readonly CallCategory[]` means a
 * value here that falls out of `callCategorySchema`'s enum fails the build
 * instead of silently dropping out unnoticed.
 *
 * Shared by two different questions that happen to have the same answer
 * TODAY, not because they are the same question:
 *   - `packages/core` (`buildAnalysisSchema`'s `isSalesAnalysis`):
 *     what counts as a sales analysis for saleProbability scoring.
 *   - `packages/prompt-builder` (`isSalesCategory`): same purpose, its own
 *     entry point.
 * (The demo TCPA synthesis was a third consumer until the built-in criteria
 * took the check over, #2856.) If the two answers ever need to diverge, SPLIT
 * this constant into one per question. Do not edit this shared list for a
 * single consumer's need; that silently changes the other.
 */
export const PURCHASE_INTENT_CATEGORIES = [
  'purchase',
  'product_inquiry',
  'scheduling',
] as const satisfies readonly CallCategory[];

/**
 * Record processing status - tracks lifecycle through the pipeline
 *
 * Status progression for successful processing:
 * 1. `pending` - Initial state, waiting to start processing
 * 2. `awaiting_media` - Waiting for late-arriving media (e.g., delayed audio upload)
 * 3. `transcribing` - Audio is being transcribed to text
 * 4. `transcribed` - Transcription complete, ready for analysis
 * 5. `analyzing` - LLM is analyzing the transcript
 * 6. `analyzed` - Analysis complete, ready for delivery
 * 7. `delivering` - Sending results to CRM/webhooks
 * 8. `delivered` - Results successfully delivered
 * 9. `completed` - Final success state, all processing complete
 *
 * Special states:
 * - `awaiting_start` - Record accepted and visible, but the organization has not
 *   turned analysis on yet. Non-terminal: leaves for `transcribing` once the
 *   owner starts analysis or processes the accumulated backlog.
 * - `awaiting_funds` - Included minutes are used up and the wallet is empty.
 *   Non-terminal and NOT an error: the record waits for a top-up rather than
 *   failing, and the pipeline does not retry — a retry would find the same
 *   empty wallet.
 * - `awaiting_chat_completion` - Chat session not yet closed in CRM, polling for completion
 * - `not_applicable` - Skipped processing (e.g., missed call, too short, no audio)
 *
 * Error states (terminal):
 * - `failed` - Generic/unknown failure
 * - `transcription_failed` - ASR provider error or invalid audio
 * - `analysis_failed` - LLM provider error or invalid transcript
 * - `delivery_failed` - CRM/webhook delivery error
 */
/**
 * Окно накопленного — ОДНО на цену и на запуск.
 *
 * `GET /records/backlog-summary` (цена) и `POST /records/process-backlog`
 * (списание) обязаны принимать один и тот же диапазон: иначе диалог обещает
 * «300 разговоров за $X», а обрабатывается другой набор. Пока это были два
 * независимых литерала в файле маршрута, расширение одного (добавить
 * `projectId`, ослабить `.datetime()`) оставляло второй узким, и расхождение
 * «показанное окно ≠ оплаченное» ловилось только на ревью. Здесь же его видит
 * и SPA — выводить свой тип ей больше неоткуда.
 */
export const backlogWindowSchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
});

export type BacklogWindow = z.infer<typeof backlogWindowSchema>;

/**
 * Котировка, которую человек видел в диалоге подтверждения и на которую
 * согласился (#1396): цена и то, за что она названа.
 *
 * Не только цена. Звонок без известной длительности входит в цену нулём
 * (спишется по длительности, измеренной на расшифровке), поэтому при той же
 * цене набор с новыми такими звонками стоит больше, чем было обещано. Сервер
 * отказывает, если по набору, который он запустит, выросло любое из трёх:
 * цена, число записей или число звонков без длительности.
 */
export const acceptedPriceSchema = z
  .object({
    priceUsd: z.number().nonnegative(),
    currency: z.string().min(1).max(8),
    records: z.number().int().nonnegative(),
    unknownDurationCalls: z.number().int().nonnegative(),
  })
  .strict();

export type AcceptedPrice = z.infer<typeof acceptedPriceSchema>;

/**
 * Заголовок, в котором ручной запуск (`/records/start`, `/records/process-
 * backlog`) несёт принятую котировку:
 * `X-Accepted-Price: <цена>;<валюта>;<записей>;<звонков без длительности>`,
 * например `0.650000;USD;12;0`.
 *
 * Заголовок, а не поле тела, — ради перекоса деплоев в ОБЕ стороны. Тела обоих
 * запросов остаются ровно теми, что принимал сервер до #1396 (тело `/start` —
 * строгая схема): новый интерфейс против старого или откаченного edge-api не
 * получает 400 на каждый запуск, старый сервер заголовок просто не читает.
 * Старый интерфейс против нового сервера заголовка не шлёт — сервер запускает
 * его, как до #1396, на время выкатки (#1457). Канал один: два (заголовок и
 * тело) потребовали бы правила, какой главнее, а поле в теле ни разу не
 * выходило в прод.
 *
 * Сам набор заголовок не несёт: у выбранных записей это их id в теле, у
 * накопленного — окно в теле с `to` = `asOf` сводки (`backlogSummarySchema`).
 *
 * Цена — с шестью знаками (`toFixed(6)`, точность биллинга): `String()`
 * печатает малые суммы экспонентой (`1e-7`), а формат обязан разбираться
 * одной регуляркой.
 */
export const ACCEPTED_PRICE_HEADER = 'X-Accepted-Price';

export function formatAcceptedPriceHeader(price: AcceptedPrice): string {
  return [
    price.priceUsd.toFixed(6),
    price.currency,
    price.records,
    price.unknownDurationCalls,
  ].join(';');
}

/** Разбор заголовка; null — заголовок испорчен (не «нет цены»). */
export function parseAcceptedPriceHeader(value: string): AcceptedPrice | null {
  const match = /^(\d+(?:\.\d{1,6})?);([A-Za-z]{1,8});(\d{1,6});(\d{1,6})$/.exec(
    value.trim()
  );
  if (!match) return null;
  const parsed = acceptedPriceSchema.safeParse({
    priceUsd: Number(match[1]),
    currency: match[2],
    records: Number(match[3]),
    unknownDurationCalls: Number(match[4]),
  });
  return parsed.success ? parsed.data : null;
}

/**
 * Потолок одного запроса `POST /api/v1/records/start` (и `/start-quote`).
 *
 * Двести — это не вкус, а предел, за которым цепочка «захват → service
 * binding → создание инстанса» перестаёт укладываться в подлимиты воркера.
 * Совпадает с серверной `MAX_BACKLOG_BATCH` (`apps/edge-api/src/routes/records/backlog.ts`)
 * намеренно дублирующим числом: схема живёт в этом пакете, а импортировать
 * ради одного числа edge-api в контракты — значит развернуть зависимость.
 * Расхождение двух чисел ловится тестом маршрута (`records-start.test.ts`).
 *
 * Экспортирована ради клиента (`useBulkRecordActions`, ui-worker): «выбрать
 * всё» может дать набор больше этого предела, и без клиентского потолка
 * запрос уходил на сервер целиком, разбиваясь об этот же `.max()` — человек
 * получал сырое сообщение валидатора вместо объяснения, что первая партия
 * запустится, а остальное подождёт.
 */
export const MAX_START_BATCH_SIZE = 200;

/**
 * POST /api/v1/records/start — запуск ВЫБРАННЫХ записей (и `/start-quote` —
 * цена того же набора). Принятая цена едет заголовком
 * `ACCEPTED_PRICE_HEADER`, а не в теле: тело совпадает с тем, что принимал
 * сервер до #1396.
 */
export const startRecordsRequestSchema = z
  .object({
    recordIds: z.array(z.string().uuid()).min(1).max(MAX_START_BATCH_SIZE),
  })
  .strict();

export type StartRecordsRequest = z.infer<typeof startRecordsRequestSchema>;

/**
 * Итог запуска. Три числа, а не одно: `queued: 0` без остальных читается как
 * тишина — ровно та тишина, из-за которой отказ партии выглядел как
 * неработающая кнопка (#400).
 */
export const startRecordsResponseSchema = z.object({
  /** Ушло в обработку этим нажатием. */
  queued: z.number().int().nonnegative(),
  /** Отвергнуто пайплайном или транспортом. */
  failed: z.number().int().nonnegative().default(0),
  /**
   * Не запускалось: записи не в запускаемом статусе, чужие или уже забранные
   * параллельным запуском. Не отказ — но и не успех, о котором можно молчать.
   */
  skipped: z.number().int().nonnegative().default(0),
  /**
   * Не запущено, потому что обработка источника записи приостановлена или
   * отозвана (установка Uspacy в `paused`/`withdrawn`, #2709). Не входит в
   * `skipped`: у этого исхода есть причина, которую интерфейс называет.
   */
  processingPaused: z.number().int().nonnegative().default(0),
});

export type StartRecordsResponse = z.infer<typeof startRecordsResponseSchema>;

/**
 * Почему повтор не запустил прогон, а оставил запись ждать ручного запуска:
 * организация в ручном режиме или в проекте выключен автоанализ — там
 * автоматический путь запись не проанализирует (#1264).
 */
export const retryWaitReasonSchema = z.enum(['manual_mode', 'project_analysis_off']);

export type RetryWaitReason = z.infer<typeof retryWaitReasonSchema>;

/**
 * Что сделает повтор сбойной записи — решение маршрута повтора, известное до
 * нажатия (#1264): `runs` — обработка запустится; `waits` — запись встанет
 * ждать ручного запуска, ничего не запустив; `needs_start` — ждать пришлось
 * бы, а запустить этот зритель не может (только клиент или админ);
 * `needs_text` — ждать пришлось бы чату без сохранённого текста;
 * `no_source` — повторять не из чего (правило сервера, клиент его не
 * пересчитывает).
 */
export const retryOutcomeSchema = z.enum([
  'runs',
  'waits',
  'needs_start',
  'needs_text',
  'no_source',
]);

export type RetryOutcome = z.infer<typeof retryOutcomeSchema>;

/**
 * Ответ `POST /records/:id/retry-transcription`. `status` различает два исхода,
 * которые человек должен прочитать по-разному: `queued` — прогон запущен,
 * `awaiting_start` — прогон НЕ запускался, запись ждёт ручного запуска с ценой.
 */
export const retryProcessingResponseSchema = z.object({
  success: z.literal(true),
  recordId: z.string(),
  status: z.enum(['queued', 'awaiting_start']),
  /**
   * How the run was started — a replay of a finished record, or a restart of
   * a failed one (the workflow itself reuses a transcript only if it was
   * paid for); absent when nothing ran (`awaiting_start`).
   */
  //
  // `reanalyze` / `full-pipeline` — what edge-api answered before #1264. Kept
  // for the rollout: ui-worker and edge-api deploy separately, and a new UI
  // against an old (or rolled-back) edge-api would otherwise reject the answer
  // of a retry that DID start — «retry failed» on a paid run. Remove them in
  // the release after #1264 is live on both workers.
  retryMode: z.enum(['replay', 'restart', 'reanalyze', 'full-pipeline']).optional(),
  waitReason: retryWaitReasonSchema.optional(),
  workflowId: z.string().optional(),
  replayRunId: z.string().optional(),
  message: z.string(),
});

export type RetryProcessingResponse = z.infer<typeof retryProcessingResponseSchema>;

/** Режим биллинга организации, как его видит котировка ручного запуска. */
export const billingModeSchema = z.enum(['prepaid', 'postpaid']);

/**
 * POST /api/v1/records/start-quote — цена выбранного набора.
 *
 * Поля повторяют `backlogSummarySchema` не случайно: диалог подтверждения один
 * и тот же, и любая разница в форме означала бы вторую формулу цены на экране.
 */
export const startQuoteResponseSchema = z.object({
  /** Сколько записей из присланных реально запускаемы. */
  records: z.number().int().nonnegative(),
  minutes: z.number().nonnegative(),
  chats: z.number().int().nonnegative(),
  /** Звонки без известной длительности — по ним интерфейс говорит «от $X». */
  unknownDurationCalls: z.number().int().nonnegative(),
  /**
   * Сколько из `records` уже оплачено при расшифровке (запись, остановленная
   * нулём баланса после неё): второго списания за них не будет, и в цену они
   * не входят. Нужно интерфейсу, чтобы нулевая цена называла настоящую
   * причину, а не «покрыто минутами тарифа» (#1389).
   *
   * `default(0)`: интерфейс, выкаченный раньше edge-api или оставшийся после
   * его отката, получит котировку без этого поля — без умолчания валидация
   * ответа отвергла бы каждую цену, и ручной запуск встал бы целиком.
   */
  alreadyCharged: z.number().int().nonnegative().default(0),
  /**
   * Сколько присланных записей ждут запуска, но не запускаемы: обработка их
   * источника закрыта (#2709). Без этого числа выборка только из таких записей
   * читалась бы как «запускать нечего», хотя они ждут. Заполняет только
   * `/start-quote`; `default(0)` — котировка сервера до этого поля.
   */
  processingPaused: z.number().int().nonnegative().default(0),
  priceUsd: z.number().nonnegative(),
  currency: z.string(),
  /**
   * Как организация платит: `postpaid` (договор) платит полную ставку мимо
   * минут тарифа, и диалог называет сумму «по договору», а не «сверх тарифа».
   * `default('prepaid')`: котировка от сервера до этого поля — прежние слова.
   */
  billingMode: billingModeSchema.default('prepaid'),
});

export type StartQuote = z.infer<typeof startQuoteResponseSchema>;

/**
 * Отказ ручного запуска (`/start` и `/process-backlog`), когда цена сейчас
 * выше той, на которую согласились (#1396). Ничего не запущено, а в теле —
 * свежая цена того же набора, чтобы интерфейс показал её, а не просил заново.
 */
export const startPriceMismatchSchema = z.object({
  error: z.string(),
  code: z.literal('START_PRICE_CHANGED'),
  quote: startQuoteResponseSchema,
});

export type StartPriceMismatch = z.infer<typeof startPriceMismatchSchema>;

/**
 * GET /api/v1/records/backlog-summary — объём накопленного до включения
 * анализа и его цена. Цена приходит с сервера и на клиенте не
 * пересчитывается: она обязана совпасть с тем, что реально спишется.
 */
/**
 * Отказ запуска или повторного анализа записи, чья установка CRM закрыла
 * обработку (`paused`/`withdrawn`, #2709): HTTP 409 с этим кодом — от edge-api
 * и от приёмника workflows-api. Один код на три сервиса: интерфейс узнаёт
 * отказ по нему и называет причину.
 */
export const INTEGRATION_PROCESSING_INACTIVE_CODE = 'INTEGRATION_PROCESSING_INACTIVE';

export const integrationProcessingInactiveRefusalSchema = z.object({
  error: z.string(),
  code: z.literal(INTEGRATION_PROCESSING_INACTIVE_CODE),
  message: z.string().optional(),
});

export type IntegrationProcessingInactiveRefusal = z.infer<
  typeof integrationProcessingInactiveRefusalSchema
>;

export const backlogSummarySchema = z.object({
  /**
   * Сколько записей уйдёт за ОДИН запуск, вместе с их минутами и ценой.
   *
   * Не весь накопленный объём: запуск обрабатывает не больше `batchLimit`
   * записей за раз. Пока сводка называла полный объём, диалог обещал «500
   * разговоров, $Y», а отправлялось 200 — обещание, которого запуск не давал.
   */
  records: z.number().int().nonnegative(),
  minutes: z.number().nonnegative(),
  /**
   * Сколько ждущих запуска записей в окне сводки не запускаемы: обработка их
   * источника закрыта (#2709). В `records`, цену и партию они не входят;
   * без этого числа накопленное только из таких записей выглядело бы пустым.
   * `default(0)` — сводка сервера до этого поля.
   */
  processingPaused: z.number().int().nonnegative().default(0),
  /**
   * Сколько из партии — чаты.
   *
   * Число появилось, когда чат стоил фиксированную цену ВНЕ гранта и минут не
   * имел: без него партия из чатов выглядела как «0 минут», а цена —
   * необъяснимой. После #414 чат расходует грантовую минуту наравне со
   * звонком и входит в `minutes`; число нужно ровно затем же, но объясняет
   * теперь обратное — откуда у переписки минуты.
   *
   * `.nullish()` по той же причине, что у `source`: `getBacklogSummary` в
   * `records.api.ts` отдаёт ответ приведением типа (`this.request<BacklogSummary>`),
   * а не рантайм-парсингом схемой — Zod здесь ничего не проверяет и не может
   * ничего обнулить. Причина в другом: ответ, собранный ДО появления этого
   * поля (кеш react-query, переживший деплой), в рантайме реально лишён
   * свойства, и обязательный тип заставил бы читающую сторону обращаться к
   * нему без проверки на `undefined`.
   */
  chats: z.number().int().nonnegative().nullish(),
  /**
   * Сколько звонков партии уйдут в анализ БЕЗ известной длительности.
   *
   * В `priceUsd` они входят нулём: сумма коалесцирует пустую длительность.
   * Списание же идёт по длительности, измеренной на транскрайбе, — файл
   * приезжает позже, и деньги за него берутся настоящие. Пока числа не было,
   * диалог называл фиксированную сумму, а партия из одних таких записей —
   * прямо «бесплатно».
   *
   * `.nullish()` по той же причине, что у `chats`: ответ, собранный до
   * появления поля, обязан остаться валидным.
   */
  unknownDurationCalls: z.number().int().nonnegative().nullish(),
  priceUsd: z.number().nonnegative(),
  currency: z.string(),
  /**
   * Срез партии: записи, принятые позже, в неё не входят (#1396). Запуск шлёт
   * его обратно как `to`, и сервер запускает ровно ту партию, что показана, —
   * без среза каждая новая запись в ручном режиме дописывалась бы в партию, и
   * сверка с показанной ценой отказывала бы снова и снова.
   *
   * `.nullish()`: ответ, собранный до поля (кеш react-query переживает
   * деплой), обязан остаться корректным; без среза запуск идёт как раньше.
   */
  asOf: z.string().datetime().nullish(),
  /** Как организация платит — см. `startQuoteResponseSchema.billingMode`. */
  billingMode: billingModeSchema.nullish(),
  /** Весь накопленный объём в выбранном окне — из него видно, что останется. */
  totalRecords: z.number().int().nonnegative(),
  /** Минуты всего накопленного (не только партии) — для заголовка карточки. */
  totalMinutes: z.number().nonnegative(),
  /** Потолок одного запуска (серверный `MAX_BACKLOG_BATCH`). */
  batchLimit: z.number().int().positive(),
  oldestAt: z.string().nullable(),
  /**
   * Состояние тумблера анализа. Едет здесь, а не берётся отдельным запросом
   * к `/settings`: тот требует `user.read` (только client/admin), и запрос
   * ради одного флага отдавал супервизорам и агентам 403 при каждом открытии
   * списка записей.
   */
  processingEnabled: z.boolean(),
  /**
   * Почему анализ на паузе, если её поставили не руками клиента, и когда.
   *
   * `.nullish()` не потому, что рантайм-валидация иначе обнулит ответ —
   * `getBacklogSummary` в `records.api.ts` берёт `this.request<BacklogSummary>`,
   * приведением типа, а эта схема здесь ничего не парсит. Поле необязательно,
   * потому что ответ, собранный ДО его появления (браузер со старым кешем
   * react-query, переживший деплой), обязан остаться корректным для читающей
   * стороны: объект в рантайме реально лишён этого свойства, и обязательный
   * тип позволил бы коду обратиться к нему без проверки на `undefined`.
   */
  pausedReason: z.string().nullish(),
  pausedAt: z.string().nullish(),
  /**
   * Может ли организация платить ПРЯМО СЕЙЧАС — вердикт `evaluateBalanceGate`
   * на момент ответа.
   *
   * Причина паузы намеренно переживает пополнение: возвращает автоматический
   * режим только человек, и след «режим отняли» нужен экрану настроек. Ложным
   * в ней было ВРЕМЯ — баннер читал «Закончились средства» в настоящем
   * времени клиенту, который уже пополнил кошелёк, и той же организации в
   * следующем цикле, со свежим грантом (#489). Это поле разводит два времени:
   * причина говорит, что случилось, оно — верно ли это ещё.
   *
   * `true` — платить есть чем (кошелёк, подарок, грант цикла или договор);
   * `false` — по-прежнему нечем; `null` — вопрос не задавался (паузы нет) или
   * биллинг не ответил. `null` обязан читаться как «не знаем», а не как
   * «средства появились»: `.nullish()` ещё и потому, что ответ, собранный до
   * появления поля, переживает деплой в кеше react-query.
   */
  pausedFundsAvailable: z.boolean().nullish(),
  /**
   * Может ли ТЕКУЩИЙ пользователь включить анализ и запустить накопленное.
   * Решает сервер тем же `organization.update`, что охраняет сами действия —
   * иначе интерфейс предлагал бы кнопку, которая ответит 403.
   */
  canStart: z.boolean(),
  /**
   * Источник, через который организация получает записи, или `null`, если
   * ничего не подключено.
   *
   * Едет здесь по той же причине, что и `processingEnabled`: отдельный
   * `/sources/active` не смонтирован в embedded-роутере, а баннер состояния
   * рисуется одним компонентом и в портале, и внутри CRM. Пока поля не было,
   * баннер заявлял «приём звонков работает: получено записей — 0» организации,
   * которая ещё не подключила ни одного источника.
   *
   * `.nullish()`, а не `.nullable()`: `getBacklogSummary` в `records.api.ts`
   * отдаёт ответ приведением типа (`this.request<BacklogSummary>`), а не
   * рантайм-парсингом схемой, так что Zod здесь ничего не обнулит. Поле
   * необязательно, потому что ответ, собранный до появления поля (кеш
   * react-query переживает деплой), обязан остаться корректным для читающей
   * стороны: объект в рантайме реально лишён этого свойства, и обязательный
   * тип позволил бы тумблеру анализа и карточке накопленного обратиться к
   * нему без проверки на `undefined`.
   */
  source: z
    .object({
      /** Ярлык вендора: 'bitrix24', 'binotel', 'public_api', … */
      vendor: z.string(),
      /** Бренд из каталога интеграций — показывается как есть, без перевода. */
      name: z.string(),
      /**
       * Чем эта установка служит организации: источником записей или только
       * источником аудио (телефония в роли `media_only` — записями владеет
       * CRM, а телефония отдаёт разговор, которого у CRM нет).
       *
       * Отсутствует в ответе, собранном до появления поля (кеш react-query
       * переживает деплой), поэтому `.nullish()` — по той же причине, что и у
       * самого `source`. Читающая сторона в этом случае обязана вести себя как
       * раньше, а не считать источник аудио отсутствующим.
       */
      role: sourceRoleSchema.nullish(),
    })
    .nullish(),
});

export type BacklogSummary = z.infer<typeof backlogSummarySchema>;

/**
 * `POST /api/v1/records/process-backlog` — что реально произошло по нажатию.
 *
 * `failed` появился потому, что его не было: запуск, отвергнутый пайплайном на
 * КАЖДОЙ записи, возвращал `queued: 0` со статусом 200, интерфейс молча
 * перерисовывал ту же карточку, и отказ читался как «кнопка не работает».
 * У sezonua так простояли 68 записей, а в логах лежало 76 отказов подряд (#400).
 * Число здесь — не украшение ответа: это единственный сигнал, по которому
 * экран может сказать «не удалось», а не промолчать.
 */
export const processBacklogResponseSchema = z.object({
  /** Сколько записей ушло в обработку. */
  queued: z.number().int().nonnegative(),
  /** Сколько записей пайплайн отверг. Ноль — норма, не отсутствие проверки. */
  failed: z.number().int().nonnegative().default(0),
  /**
   * Сколько записей приёмник отверг закрытой обработкой источника (#2709):
   * пауза легла между отбором партии и запуском. Не входит в `failed` —
   * у отказа своя причина, и интерфейс её называет.
   */
  processingPaused: z.number().int().nonnegative().default(0),
  /** Осталось ли накопленное за пределами этой партии (`batchLimit`). */
  remaining: z.enum(['more', 'none']),
});

export type ProcessBacklogResponse = z.infer<typeof processBacklogResponseSchema>;

export const recordStatusSchema = z.enum([
  'pending',
  'awaiting_start',
  'awaiting_funds',
  'awaiting_media',
  'awaiting_chat_completion', // Chat session not yet closed in CRM, polling for completion
  'not_applicable',
  'transcribing',
  'transcribed',
  'analyzing',
  'analyzed',
  'delivering',
  'delivered',
  'partially_delivered',
  'completed',
  'failed',
  'delivery_failed',
  'transcription_failed',
  'analysis_failed',
  'pii_redacted',
  'processing_objected',
]);

/**
 * Statuses at which a record has stopped for good, having failed.
 *
 * SSOT for a grouping two independent consumers used to keep by hand: the
 * dashboard/analytics SQL in `@aica/database`
 * (`repositories/record-status-sets.ts`) and the System Health tiles in
 * `apps/ui-worker` (`lib/records/status.ts`). When the two lists disagreed,
 * "Total failed" stopped equalling the sum of its tiles and nothing went red,
 * because the packages share no dependency to compare them through — they do
 * share this one, where the status vocabulary itself is defined.
 *
 * `satisfies` is what makes it a guard: a member that is not a `RecordStatus`
 * fails to compile.
 */
export const TERMINAL_FAILURE_RECORD_STATUSES = [
  'failed',
  'delivery_failed',
  'transcription_failed',
  'analysis_failed',
] as const satisfies readonly z.infer<typeof recordStatusSchema>[];

/**
 * Supported language codes (ISO 639-1 preferred).
 * Used for transcript language, project default language, org notification language.
 */
export const languageCodeSchema = z
  .string()
  .min(2)
  .max(10)
  .regex(
    /^[a-z]{2}([_-][A-Za-z]{2})?$/,
    'Must be ISO 639-1 (e.g. "en") or BCP 47 (e.g. "en-US")'
  );

// Alert severity: canonical definition in alerts.ts, re-exported from schemas/index.ts

// ============================================================================
// Common Schemas
// ============================================================================

// NOTE: For /records/:id routes, use uuidParamSchema from common.ts
// The recordIdParamSchema in common.ts is for nested routes like /:orgId/records/:recordId

// ============================================================================
// List Records Schemas
// ============================================================================

/**
 * Query params for GET /api/v1/records
 *
 * Supports filtering, pagination, and sorting of records.
 * All filters are optional and can be combined:
 * - `projectId`: Filter to specific project (UUID) — deprecated, see `projectIds`
 * - `projectIds`: Filter to a set of projects (project-multiselect K5); wins
 *   over `projectId` whenever present and non-empty, the two are never merged.
 *   Powers drill-down from the dashboard's project filter into this list.
 * - `status`: Filter by processing status (e.g., 'completed', 'failed')
 * - `type`: Filter by channel type ('call', 'chat', 'email')
 * - `crmEntityType`: Filter by associated CRM entity ('deal', 'lead', etc.)
 * - `limit`: Max records per page (1-1000, default: 100)
 * - `offset`: Pagination offset (default: 0)
 *
 * Example: GET /api/v1/records?projectIds=xxx,yyy&status=completed&limit=50
 */
/**
 * Строки воронки «Головне», которые список умеет открыть (#1108): её корзины и
 * `unclassified` — «Інші статуси», запись, которую воронка не разложила ни в
 * одну корзину. Без неё сумма списков не сходилась бы с итогом воронки.
 */
export const RECORD_OUTCOME_FILTERS = [...REPORT_FUNNEL_BUCKETS, 'unclassified'] as const;
export const recordOutcomeFilterSchema = z.enum(RECORD_OUTCOME_FILTERS);
export type RecordOutcomeFilter = z.infer<typeof recordOutcomeFilterSchema>;

/**
 * Номер сущности CRM — строка цифр, как его отдают Bitrix24 и Uspacy (`ID`
 * карточки). Не число: 20 цифр не влезают в `Number` без потери точности.
 */
export const CRM_ENTITY_ID_PATTERN = /^\d{1,20}$/;

/**
 * Поля запроса списка без правил между полями. Отдельно от
 * `listRecordsQuerySchema` потому, что `.omit` у Zod не работает на объекте с
 * уточнением: схема фильтров семантического поиска выводится из ЭТИХ полей и
 * накладывает то же уточнение сама (`refineListRecordsQuery`).
 */
export const listRecordsQueryFieldsSchema = z.object({
  /** @deprecated legacy single-project filter — kept for one release cycle
   *  (SPA and edge-api deploy separately) so a stale bundle still filters.
   *  Ignored whenever `projectIds` is present and non-empty. */
  projectId: z.string().uuid().optional(),
  projectIds: projectIdsFilterSchema,
  status: recordStatusSchema.optional(),
  /**
   * Исход воронки «Головне» — те же корзины и то же правило
   * (`classifyFunnelRow`), что у воронки (#1108), плюс её строка «Інші
   * статуси». Независим от `status`: корзина решает по статусу, анализу,
   * транскрипту и признаку короткого.
   */
  outcome: recordOutcomeFilterSchema.optional(),
  type: recordTypeSchema.optional(),
  crmEntityType: crmEntityTypeSchema.optional(),
  /**
   * Номер сущности CRM (#743) — вкладка Aelo в карточке сделки, лида,
   * контакта, компании. Только вместе с `crmEntityType`: номера разных типов
   * пересекаются (сделка #123 и контакт #123 — разные сущности). Сужает
   * выдачу поверх границы видимости, а не заменяет её. Только во встроенной
   * сессии CRM, где выдача сужается до её установки: вне неё — 400, потому
   * что без портала номер неоднозначен между порталами организации и
   * демо-установкой. `crmEntityType` без номера работает и вне встроенной
   * сессии, как прежде.
   */
  crmEntityId: z.string().regex(CRM_ENTITY_ID_PATTERN).optional(),
  /**
   * Вкладка сделки (#745): добавить разговоры лида, из которого выросла
   * сделка. Только включает подмешивание — какой лид, сервер берёт из цепочки
   * состояний сделки, не из запроса. Для не-сделки игнорируется. Postgres не
   * ответил — список остаётся одной сделкой, а ответ несёт
   * `sourceLeadsUnavailable`. Enum, а не `z.coerce.boolean()`: тот читает
   * `'false'` как true.
   */
  includeSourceLead: z.enum(['true', 'false']).optional(),
  callCategory: callCategorySchema.optional(),
  seriesId: z.string().optional(),
  /**
   * Filter by call direction. `unknown` is the flow's own third value
   * (`dashboardFlowDirectionSchema`): no direction, `unknown`, or another word —
   * the set the dashboard counts as «direction unknown» (#2562), so a number
   * on the dashboard opens exactly its records.
   */
  callDirection: dashboardFlowDirectionSchema.optional(),
  /** Filter by specific agent (for managers filtering by team member) */
  agentId: z.string().optional(),
  excludeMissed: z.coerce.boolean().optional(),
  /**
   * Hide short calls (#876) — the records the product marks short: the
   * `isShortCall` flag OR the `short` category (Bitrix24 sets only the latter).
   * An enum, not `z.coerce.boolean()`: that reads `'false'` as true.
   */
  excludeShort: z.enum(['true', 'false']).optional(),
  /**
   * Only the caller's own records, whatever else their role may see — the
   * list behind the quality slice's «my score» (#1235). An enum, not
   * `z.coerce.boolean()`: that reads `'false'` as true.
   */
  ownRecords: z.enum(['true', 'false']).optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(100),
  offset: z.coerce.number().int().min(0).default(0),
  /** Full-text search across participant name, phone numbers, agent name, transcript preview, external ID */
  search: z.string().max(RECORD_SEARCH_MAX_LENGTH).optional(),
  /**
   * Курсор порций поиска из прошлого ответа (#980): набор продолжается от него,
   * а не от начала совпадений. Без него — прежняя пагинация по `offset`.
   */
  searchCursor: searchCursorParamSchema.optional(),
  /** Filter records created on or after this ISO 8601 datetime (inclusive) */
  createdAfter: z.string().datetime({ offset: true }).optional(),
  /** Filter records created on or before this ISO 8601 datetime (inclusive) */
  createdBefore: z.string().datetime({ offset: true }).optional(),
  /** Filter by agent's rating of AI analysis accuracy (1–5) */
  agentRating: z.coerce.number().int().min(1).max(5).optional(),
  /** Comparison operator for agentRating filter */
  agentRatingMode: z.enum(['gte', 'eq', 'lte']).optional(),
  /**
   * Filter by the canonical quality score of the current analysis (the one-decimal
   * sub-metric mean, #1203) — lower bound, inclusive (0-100). `0` sets no bound.
   */
  qaScoreMin: z.coerce.number().int().min(0).max(100).optional(),
  /**
   * Upper bound over the same one-decimal score, HALF-OPEN: `M` matches scores
   * `< M + 1` (so "0–79" includes 79.5 and "80–100" does not). `100` sets no
   * bound: sub-metrics are not range-checked, and a mean above 100 still
   * belongs to the top band its row's sentiment names. Changed with #1203 —
   * before it, `M` matched the rounded score `<= M`.
   */
  qaScoreMax: z.coerce.number().int().min(0).max(100).optional(),
  /** Filter by derived sentiment; translated to a quality-score range server-side */
  sentiment: z.enum(['positive', 'neutral', 'negative']).optional(),
  /**
   * Условия по критериям (#865): повторяющийся `criterion`, до трёх, «И».
   * Формат строки — `criteria-filter.ts`.
   */
  criterion: recordCriteriaFilterSchema,
  /**
   * «Шаг скрипта не выполнен» (#879): `<stepId>:missed`, один шаг за раз.
   * Формат строки — `script-step-filter.ts`.
   */
  scriptStep: recordScriptStepQuerySchema,
  /**
   * Whether to include demo records in the response.
   * - 'yes' (default): include demo records alongside real records
   * - 'no': exclude demo records (show only real records)
   * Note: 'only' (demo-only) is internal and not exposed via API.
   */
  includeDemo: z.enum(['yes', 'no']).default('yes'),
});

/** Правила между полями запроса списка — общие для списка и семантического поиска. */
export function refineListRecordsQuery(
  query: { crmEntityId?: string; crmEntityType?: string },
  ctx: z.RefinementCtx
): void {
  if (query.crmEntityId !== undefined && query.crmEntityType === undefined) {
    ctx.addIssue({
      code: 'custom',
      path: ['crmEntityType'],
      message: 'crmEntityId requires crmEntityType',
    });
  }
}

export const listRecordsQuerySchema =
  listRecordsQueryFieldsSchema.superRefine(refineListRecordsQuery);

/**
 * Record list item - matches API response from GET /api/v1/records
 *
 * Includes all fields returned by the API for list views.
 * Optimized for table/list displays with essential fields + preview data.
 *
 * Non-obvious fields:
 * - `stage`: Sales/pipeline stage from CRM (e.g., "Qualification", "Proposal")
 *   Extracted from CRM metadata during record processing. Null if not linked to CRM deal/lead.
 *
 * - `stageStatus`: Status within the current stage (e.g., "In Progress", "Won", "Lost")
 *   Provides more granular state than just the stage. Null if not available from CRM.
 *
 * - `isShortCall`: True if the record is below its project's significance
 *   threshold and therefore kept but not analyzed — for type='call', duration
 *   under `minCallDurationSeconds`; for type='chat', fewer dialogue messages
 *   than `minChatMessages` or no operator reply at all.
 *   The name is historical: chats deliberately reuse this flag (the same one
 *   already gates analysis, CRM delivery and call series), so do not read it
 *   as "calls only". Null for type='email' and for records not yet evaluated.
 *
 * - `transcriptPreview`: First ~200 chars of transcript for quick preview
 *   Truncated text to show in list views without loading full transcript.
 *
 * - `agentId`/`agentName`: The agent/rep who handled this interaction
 *   Extracted from CRM metadata (e.g., Bitrix24 ASSIGNED_BY_ID field).
 *   Used for agent performance tracking and filtering.
 */
export const recordListItemSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string(),
  projectId: z.string(),
  installationId: z.string().nullable(),
  type: recordTypeSchema,
  externalId: z.string().nullable(),
  status: recordStatusSchema,
  durationSeconds: z.number().nullable(),
  participantName: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  archived: z.boolean().nullable(),
  transcriptPreview: z.string().nullable(),
  agentId: z.string().nullable(),
  agentName: z.string().nullable(),
  projectName: z.string().nullable(),
  /** Sales/pipeline stage from CRM (e.g., "Qualification", "Proposal"). Null if not linked to CRM deal/lead. */
  stage: z.string().nullable(),
  /** Status within current stage (e.g., "In Progress", "Won", "Lost"). Null if not available. */
  stageStatus: z.string().nullable(),
  // CRM Entity fields
  crmActivityName: z.string().nullable(),
  crmEntityType: crmEntityTypeSchema.nullable(),
  crmEntityId: z.string().nullable(),
  crmEntityName: z.string().nullable(),
  /**
   * Which CRM this record came from, and the portal its card lives in.
   *
   * Resolved from the record's own `installationId`, NEVER from the org. An
   * org can hold a Bitrix24 install and a Uspacy install at once, and the
   * deprecated `orgs.bitrix24Domain` used to answer for both — so a Uspacy
   * record's "open in CRM" link pointed into the Bitrix24 portal carrying a
   * Uspacy entity id, i.e. at a real card belonging to someone else.
   *
   * Deliberately NOT an enum: `embedded_installs.crm_vendor` also holds
   * telephony vendor ids, whose records have no entity card at all. The link
   * builder returns null for any vendor without a known URL shape, which is
   * the behaviour we want — plain text beats a link into the wrong CRM.
   *
   * Both null together = we cannot name a portal; render plain text.
   */
  crmVendor: z.string().nullable().optional(),
  crmDomain: z.string().nullable().optional(),
  // Stage snapshot fields (Bitrix24 deal/lead stage at call time, migration 060)
  /** STAGE_ID at call time. Null until stage capture runs (Phase B). */
  crmStageId: z.string().nullable().optional(),
  /** Bitrix24 CATEGORY_ID (pipeline/воронка). Distinct from callCategory. */
  crmPipelineId: z.string().nullable().optional(),
  /** Coarse-grained stage bucket (discovery/qualification/proposal/...). */
  crmStageSemantic: crmStageSemanticSchema.nullable().optional(),
  /** ISO-8601 timestamp of when the stage snapshot was captured. */
  crmStageCapturedAt: z.string().nullable().optional(),
  /** How the stage snapshot was captured (live/stagehistory/unknown). */
  crmStageSource: crmStageSourceSchema.nullable().optional(),
  /**
   * Where `crmStageSemantic` came from (#1206) — `name` is a guess. A value
   * this build does not know reads as null instead of failing the whole
   * records page; a phase with a null origin was written before #1206 and
   * came from the CRM.
   */
  crmStageSemanticOrigin: crmStageSemanticOriginSchema.nullable().optional().catch(null),
  /**
   * True when the record is below its project's significance threshold and is
   * kept but not analyzed: a call shorter than `minCallDurationSeconds`, or a
   * chat under `minChatMessages` / with no operator reply. The name is
   * historical — chats reuse the same flag. Null for email and for records the
   * filter has not evaluated yet.
   */
  isShortCall: z.boolean().nullable(),
  /** Automatic call category (product_inquiry, support, billing, etc.). Null if not yet categorized. */
  callCategory: callCategorySchema.nullable(),
  /** Call direction from CRM (inbound/outbound). Null if not available. */
  callDirection: z.enum(['inbound', 'outbound']).nullable().optional(),
  /** Event time from CRM (START_TIME from Bitrix24). The actual time of the call/chat. */
  eventTime: z.string().nullable().optional(),
  /**
   * Canonical quality score of the current analysis: the one-decimal mean of the
   * six sub-metrics (0-100), the number the dashboard averages (#1203). Null when
   * unanalyzed or when the analysis carries no sub-metric.
   */
  qualityScore: z.number().nullable().optional(),
  /** Predicted sale probability (0-100) from latest analysis. Calls only; null for chats and unscored records. */
  saleProbability: z.number().int().min(0).max(100).nullable().optional(),
  /** Sentiment derived from the canonical quality score (>=80 positive, >=50 neutral, <50 negative). Null when unscored. */
  sentiment: z.enum(['positive', 'neutral', 'negative']).nullable().optional(),
  /** A-F component scores from latest analysis (0-100). Null when the metric is absent (e.g. scoreless categories). */
  greetingScore: z.number().int().min(0).max(100).nullable().optional(),
  needsScore: z.number().int().min(0).max(100).nullable().optional(),
  objectionScore: z.number().int().min(0).max(100).nullable().optional(),
  closingScore: z.number().int().min(0).max(100).nullable().optional(),
  politenessScore: z.number().int().min(0).max(100).nullable().optional(),
  clarityScore: z.number().int().min(0).max(100).nullable().optional(),
  /** ID of the latest analysis. Lets the detail panel fetch the analysis in parallel with record details. */
  analysisId: z.string().nullable().optional(),
  // Call series fields
  callSeriesId: z.string().nullable().optional(),
  callSeriesNumber: z.number().int().min(1).nullable().optional(),
  callSeriesEntityType: z.enum(['deal', 'lead']).nullable().optional(),
  callSeriesEntityId: z.string().nullable().optional(),
  callSeriesTotal: z.number().int().nullable().optional(),
  // Phone numbers (extracted from CRM COMMUNICATIONS)
  agentPhone: z.string().nullable().optional(),
  participantPhone: z.string().nullable().optional(),
  /** Agent's rating of AI analysis accuracy (1–5 stars). Null if not yet rated. */
  agentRating: z.number().int().min(1).max(5).nullable().optional(),
  /** Optional text comment left alongside the star rating */
  agentRatingText: z.string().nullable().optional(),
  /** ISO-8601 timestamp of the last rating action */
  agentRatingAt: z.string().nullable().optional(),
  /** Clerk userId of the person who submitted the rating */
  agentRatingUserId: z.string().nullable().optional(),
  agentRatingUserName: z.string().nullable().optional(),
  /** True if this record was created as demo/sample data. Defaults to false. */
  isDemo: z.boolean().default(false),
  /** Deal outcome from call_series_outcomes (Phase C, C-1). Null when the record isn't a deal series or the outcome hasn't synced yet. */
  dealOutcome: callSeriesOutcomeSemanticSchema.nullable().optional(),
});

/** Schema for PATCH /api/v1/records/:id/agent-rating */
export const updateAgentRatingSchema = z.object({
  rating: z.number().int().min(1).max(5).nullable(),
  feedback: z.string().max(1000).nullable().optional(),
});

export const listRecordsResponseSchema = z.object({
  records: z.array(recordListItemSchema),
  total: z.number().int(),
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
  /** Запрос пришлось укоротить под предел D1 — см. `searchTruncatedToSchema`. */
  searchTruncatedTo: searchTruncatedToSchema,
  /** `total` неполон — см. `searchTotalIsPartialSchema`. */
  searchTotalIsPartial: searchTotalIsPartialSchema,
  /** Выдачу дал запасной D1 LIKE — см. `searchFallbackSchema`. */
  searchFallback: searchFallbackSchema,
  /** Передать в следующий запрос — см. `searchCursorSchema`. Нет поля — совпадения исчерпаны или поиск не порциями. */
  searchCursor: searchCursorResponseSchema,
  /** Шаг, применённый фильтром `scriptStep`, — см. `recordScriptStepFilterEchoSchema`. Нет фильтра — нет поля. */
  scriptStepFilter: recordScriptStepFilterEchoSchema.optional(),
  /**
   * Просили лиды сделки (`includeSourceLead`, #745), а Postgres не ответил
   * или не уложился в срок: выдача — разговоры одной сделки. Нет поля — лиды
   * прочитаны, их нет или их не просили.
   */
  sourceLeadsUnavailable: z.literal(true).optional(),
});

/** Одно значение выпадающего фильтра: чем фильтруем и что показываем. */
export const filterOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
});

/**
 * Запрос значений фильтров. Только `includeDemo` — список НЕ сужается
 * остальными активными фильтрами намеренно: он должен быть стабильным, иначе
 * человек «пропадает» из выпадашки при смене периода, то есть ровно та жалоба,
 * ради которой маршрут появился. Демо-записи исключение: они принадлежат не
 * людям, а посеянному набору, и их агенты в списке живых операторов лишние.
 */
export const recordsFilterOptionsQuerySchema = z.object({
  includeDemo: z.enum(['yes', 'no']).default('yes'),
});

/**
 * Запрос перечня критериев фильтра (`criteria-filter-options`). Демо-критерии
 * (#1502) отвечены только на демо-звонках: перечень отдаёт их только при
 * `includeDemo=yes`. Без параметра — нет: так спрашивает интерфейс, собранный
 * до демо-критериев, и демо-критериев он не знал.
 */
export const recordsCriteriaFilterOptionsQuerySchema = z.object({
  includeDemo: z.enum(['yes', 'no']).optional(),
});

/**
 * Значения динамических фильтров списка записей — GET /api/v1/records/filter-options.
 *
 * Считаются в ТОЙ ЖЕ границе видимости, что применяется к самим записям.
 * Раньше обе выпадашки наполнялись на клиенте из подгруженных строк, поэтому
 * их содержимое зависело от глубины прокрутки: агент со звонком пятидневной
 * давности в списке не появлялся, хотя его записи были доступны и находились
 * поиском.
 *
 * Здесь только динамические значения. Фиксированные перечисления (канал,
 * направление, категория, статус, тип CRM-сущности, рейтинг, тональность)
 * живут в коде и сервера не требуют.
 */
export const recordsFilterOptionsResponseSchema = z.object({
  agents: z.array(filterOptionSchema),
  projects: z.array(filterOptionSchema),
  /**
   * @deprecated Перечень критериев — `GET /records/criteria-filter-options`
   * (#1293). Здесь на время выкладки: его читают вкладки со сборкой из #865, а
   * новый интерфейс — только когда нового маршрута нет (откат edge-api).
   * Убрать после релиза вместе с этим запасным чтением — #1314.
   */
  criteria: z.array(criterionFilterOptionSchema).default([]),
});

// ============================================================================
// Create/Update Record Schemas
// ============================================================================

/**
 * Create record request - POST /api/v1/records
 *
 * Creates a new record for processing. Supports two main workflows:
 *
 * 1. **Voice call with audio** (type='call' + audioUrl):
 *    - Provide `audioUrl` pointing to audio file (mp3, wav, etc.)
 *    - System will transcribe audio → analyze → deliver results
 *    - Example: { projectId, type: 'call', audioUrl: 'https://...', participantName: 'John Doe' }
 *
 * 2. **Chat/email with existing transcript** (type='chat'/'email' + transcript):
 *    - Provide `transcript` as plain text
 *    - System skips transcription, goes directly to analysis
 *    - Example: { projectId, type: 'chat', transcript: 'Hello, I need help with...', participantName: 'Jane Smith' }
 *
 * 3. **Voice call without immediate audio** (type='call', no audioUrl):
 *    - Used when audio upload is delayed (e.g., Bitrix24 async upload)
 *    - Record enters 'awaiting_media' status
 *    - Audio provided later via separate upload endpoint
 *
 * Required fields:
 * - `projectId`: Project/workspace this record belongs to
 * - `type`: Channel type ('call', 'chat', 'email')
 *
 * Optional fields:
 * - `audioUrl`: URL to audio file (for type='call' with immediate audio)
 * - `transcript`: Pre-existing transcript text (for type='chat'/'email', or type='call' if already transcribed)
 * - `participantName`: Customer/contact name (for display and filtering)
 * - `externalId`: CRM activity ID for deduplication and linking
 * - `metadata`: Arbitrary JSON for custom fields (e.g., CRM deal info, tags)
 *
 * Note: At least one of `audioUrl` or `transcript` should be provided for immediate processing.
 * If neither is provided, record will be created in 'awaiting_media' status.
 */
export const createRecordRequestSchema = z
  .object({
    projectId: z.string(),
    type: recordTypeSchema,
    audioUrl: z.string().url().optional(),
    transcript: z.string().optional(),
    participantName: z.string().optional(),
    externalId: z.string().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

export const updateRecordRequestSchema = z
  .object({
    status: recordStatusSchema.optional(),
    participantName: z.string().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

/**
 * Schema for uploading a record via JSON body
 */
export const uploadRecordRequestSchema = z
  .object({
    projectId: z.string().min(1),
    installationId: z.string().min(1).optional(),
    type: recordTypeSchema,
    externalId: z.string().optional(),
    fileUrl: z.string().url().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

/**
 * Schema for re-analyzing a record
 */
export const replayAuthorizationSchema = z
  .object({
    /** SHA-256 of the canonical, validated bulk replay manifest. */
    manifestFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    /** External approval/change-ticket reference; not an authentication claim. */
    approvalReference: z.string().trim().min(1).max(255),
    /** Human-readable approver asserted by the manifest. The authenticated caller wins. */
    authorizedBy: z.string().trim().min(1).max(255),
    authorizedAt: z.string().datetime({ offset: true }),
    estimatedTotalCostCents: z.number().int().positive(),
    authorizedCostCeilingCents: z.number().int().positive(),
    estimatedItemCostCents: z.number().int().positive(),
    currency: z.literal('USD'),
    recordCount: z.number().int().min(1).max(100),
    /** 1-based position in the canonical manifest. */
    itemIndex: z.number().int().min(1).max(100),
    costEstimatedBy: z.string().trim().min(1).max(255),
    costEstimatedAt: z.string().datetime({ offset: true }),
    costEstimationMethod: z.string().trim().min(1).max(500),
  })
  .strict()
  .superRefine((authorization, ctx) => {
    if (
      authorization.estimatedTotalCostCents > authorization.authorizedCostCeilingCents
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['estimatedTotalCostCents'],
        message: 'estimated total exceeds the authorized cost ceiling',
      });
    }
    if (authorization.estimatedItemCostCents > authorization.estimatedTotalCostCents) {
      ctx.addIssue({
        code: 'custom',
        path: ['estimatedItemCostCents'],
        message: 'item estimate exceeds the manifest total',
      });
    }
    if (authorization.itemIndex > authorization.recordCount) {
      ctx.addIssue({
        code: 'custom',
        path: ['itemIndex'],
        message: 'item index exceeds the manifest record count',
      });
    }
  });

export const reanalyzeRequestSchema = z
  .object({
    promptProfileId: z.string().uuid().optional(),
    mode: replayModeSchema.default('from_asr_source'),
    publish: analysisPublicationSchema.default('promote'),
    /** Required only for analysis_only; from_asr_source is resolved server-side. */
    transcriptId: z.string().uuid().optional(),
    /** null explicitly asserts that the record has no current analysis. */
    expectedCurrentAnalysisId: z.string().uuid().nullable().optional(),
    /** Stable per user/ops action; transport retries reuse the same paid run. */
    operationId: z.string().uuid().optional(),
    /** Optional bulk-operation provenance, durably audited by the API. */
    authorization: replayAuthorizationSchema.optional(),
    /** Replay is analysis-only remediation; CRM delivery requires review. */
    deliverToCrm: z.literal(false).default(false),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (data.mode === 'analysis_only' && !data.transcriptId) {
      ctx.addIssue({
        code: 'custom',
        path: ['transcriptId'],
        message: 'transcriptId is required for analysis_only replay',
      });
    }
    if (data.mode === 'from_asr_source' && data.transcriptId) {
      ctx.addIssue({
        code: 'custom',
        path: ['transcriptId'],
        message: 'from_asr_source resolves the lineage root server-side',
      });
    }
  });

/**
 * Ceiling for any file sent as a multipart body — the public API and the SPA
 * alike.
 *
 * It lives here, in the shared contract, because it is not a policy either
 * side gets to hold its own opinion about: reading multipart parts requires
 * `Request.formData()`, which materializes the whole body in memory, and the
 * Workers isolate has ~128 MB shared across concurrent requests. A larger cap
 * is not a cap at all — the isolate dies before the check enforcing it can
 * run, taking unrelated in-flight requests with it.
 *
 * The number therefore has to be the same everywhere the limit is stated:
 * server gates, and any UI that tells a user what will be accepted. Two
 * literals that merely agree today will drift, and the UI is where that drift
 * costs most — it had promised 500 MB, inviting an upload that could only ever
 * end in a dead isolate.
 *
 * 25 MB is over three hours of speech at 16 kbps, far beyond any real call.
 * Larger media goes through POST /records with `fileUrl`, which streams
 * straight to storage and never holds the file — see
 * MAX_FILE_URL_FETCH_BYTES below.
 */
export const MAX_MULTIPART_UPLOAD_BYTES = 25 * 1024 * 1024; // 25 MB

/**
 * Ceiling for audio Aelo DOWNLOADS from a `fileUrl`.
 *
 * Four times the multipart cap, and safe at that size for one reason: the
 * downloader streams the response into storage through a capped stream and
 * aborts mid-flight, so the Worker never holds the file. Do NOT reuse it for
 * multipart — see MAX_MULTIPART_UPLOAD_BYTES above for why that crashes.
 *
 * It lives here rather than beside the downloader because the upload modal now
 * states it to the user: when a file is refused for being too large, the
 * message offers the link route and has to name the ceiling of that route too.
 * An unconditional "send it as a link instead" is false at 200 MB, and the
 * user would only find out on the docs page we sent them to. Once a number is
 * spoken to a user it belongs in the shared contract — the same lesson the two
 * caps above are written from.
 */
export const MAX_FILE_URL_FETCH_BYTES = 100 * 1024 * 1024; // 100 MB

/**
 * Ceiling for a chat transcript sent as a multipart part.
 *
 * Tighter than MAX_MULTIPART_UPLOAD_BYTES because the chat branch reads the
 * part a SECOND time in full — `JSON.parse(await file.text())` — on top of the
 * copy `formData()` already materialized. Its peak cost is therefore roughly
 * twice the body, where audio is streamed to storage from the copy in hand.
 *
 * Here for the same reason as the audio cap: server and upload modal must
 * state one number, not two that happen to agree. They did happen to agree —
 * and that is not a property worth relying on, it is the same setup that let
 * the audio pair drift to 500 MB against a server accepting 25.
 *
 * The FILE (or the public request body), not the text: an export carries
 * ids, timestamps and escapes around the text it yields, so a 3 MB file with
 * 300 KB of text is a chat to store (owner decision on #1489: stored, not
 * analysed). The text itself is held to `MAX_STORED_CHAT_TEXT_BYTES`.
 */
export const MAX_CHAT_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * The most chat text (UTF-8 bytes) a record can store.
 *
 * `records_metadata.text_content` lives in one D1 row, and D1 refuses a row
 * over 2,000,000 bytes — before #1489 a larger chat reached the insert and
 * came back as a 500. Measured on the text as stored (`storedChatTextExceeds`):
 * write-time redaction can grow a text (`a@b.co` → `[EMAIL]`). The 100 000
 * bytes left under the row limit hold the rest of the row (the 500-character
 * preview, names, ids). Intake (upload, public API) answers 413 above it,
 * before the row is written.
 */
export const MAX_STORED_CHAT_TEXT_BYTES = 1_900_000;

/**
 * The most a caller's record fields — names, phones, ids, CRM names: every
 * string a public upload writes into the same D1 row besides the chat text —
 * may take together, in UTF-8 bytes (#1537).
 *
 * The row holds at most 2,000,000 bytes; the text takes up to
 * `MAX_STORED_CHAT_TEXT_BYTES`, and the fields share the rest with what Aelo
 * writes itself (ids, timestamps, the 500-character preview — under 3 KB).
 * Unbounded, a 1.9 MB chat with 200 KB of names passed every check and failed
 * at the insert with a 500. 64 KB is far above any real name or id and keeps
 * the whole row under the limit. Intake (`POST /records`, multipart upload)
 * answers 413 above it, before the row is written.
 */
export const MAX_RECORD_FIELDS_BYTES = 64 * 1024;

/** UTF-8 bytes of the string values among `fields` — what they take in the row. */
export function recordFieldsByteLength(fields: Record<string, unknown>): number {
  let bytes = 0;
  for (const value of Object.values(fields)) {
    if (typeof value === 'string') bytes += utf8ByteLength(value);
  }
  return bytes;
}

/**
 * The most chat text (UTF-8 bytes) Aelo analyses (#1489, owner decision
 * 02.10).
 *
 * A chat is billed at a flat price, while its cost to us grows with its
 * length (≈ 0.25 ¢ + 0.23 ¢ per KB). A longer chat — from any source — is
 * stored and visible in Records but is not analysed and not charged: the
 * pipeline closes it as `not_applicable` with the reason `chat_too_long`
 * (`REASON_CHAT_TOO_LONG`, `@aica/core`). Prod on 02.10 (591 chats in a year):
 * p99 6.5 KB, largest 9.4 KB, so the cap touches no real chat today.
 *
 * Measured in ONE place on ONE copy: the bytes of the stored D1
 * `text_content` (trimmed, write-time redacted), by the SQL in
 * `@aica/database` (`text-presence.ts`) that the workflow, the
 * startable-backlog query and the start all ask. Intake only predicts it to
 * warn early (`storedChatTextSurelyWithin`).
 */
export const MAX_ANALYZED_CHAT_BYTES = 100 * 1024; // 100 KB

const utf8 = new TextEncoder();

/** Size of a string in UTF-8 bytes — what D1 stores and what the caps count. */
export function utf8ByteLength(text: string): number {
  return utf8.encode(text).byteLength;
}

/**
 * Is `text` longer than `maxBytes` in UTF-8? Decided from the string's length
 * where that is enough — a UTF-16 unit is one to three bytes (a surrogate
 * pair, two units, is four) — and encoded only in between.
 */
export function utf8ByteLengthExceeds(text: string, maxBytes: number): boolean {
  if (text.length > maxBytes) return true;
  if (text.length * 3 <= maxBytes) return false;
  return utf8ByteLength(text) > maxBytes;
}

/**
 * The audio formats a person may upload, and the MIME types each arrives as.
 *
 * One list, for the same reason as the size caps above: the modal tells the
 * user what will be accepted, the docs promise it, and a validator decides it.
 * Three statements of one fact drift — and these had. The docs promised six
 * formats, the modal's own literals accepted three (`.mp3 .wav .m4a`, written
 * beside the 500 MB cap that later proved wrong), and the upload route took
 * anything `audio/*`. A customer who read the docs and dragged in an OGG was
 * refused by a product that could have transcribed it.
 *
 * Why these six: ElevenLabs Scribe — the only ASR provider wired to a
 * credential loader (`packages/core/src/provider-credentials.ts`) — reads all
 * of them, and AAC, AIFF and Opus besides. The six are what the documentation
 * already promises and what call recorders and telephony actually produce; the
 * rest can be added here, and only here, when something asks for them.
 *
 * The extension is the gate. The MIME type is checked second and softly: a
 * browser may report nothing, or something unexpected, for a container it does
 * not know.
 *
 * `scripts/check-upload-formats.mjs` holds the documentation to this list.
 */
export const ACCEPTED_AUDIO_FORMATS = [
  { extension: '.mp3', mimeTypes: ['audio/mpeg', 'audio/mp3'] },
  { extension: '.wav', mimeTypes: ['audio/wav', 'audio/x-wav', 'audio/wave'] },
  { extension: '.m4a', mimeTypes: ['audio/mp4', 'audio/x-m4a'] },
  { extension: '.ogg', mimeTypes: ['audio/ogg', 'audio/opus'] },
  { extension: '.webm', mimeTypes: ['audio/webm'] },
  { extension: '.flac', mimeTypes: ['audio/flac', 'audio/x-flac'] },
] as const;

/** Extensions the upload modal accepts, in the order the docs list them. */
export const ACCEPTED_AUDIO_EXTENSIONS: readonly string[] = ACCEPTED_AUDIO_FORMATS.map(
  (format) => format.extension
);

/** Every MIME type those extensions can arrive as. */
export const ACCEPTED_AUDIO_MIME_TYPES: readonly string[] =
  ACCEPTED_AUDIO_FORMATS.flatMap((format) => [...format.mimeTypes]);

/** `audio/ogg; codecs=opus` → `audio/ogg`: servers append parameters. */
function normalizeMimeType(value: string): string {
  return (value.split(';')[0] ?? '').trim().toLowerCase();
}

/**
 * What to tell the ASR provider the audio is.
 *
 * Two kinds of caller, and one rule has to serve both.
 *
 * A browser upload's type is only as good as the browser: for a container it
 * does not know it reports nothing, and the uploader stores
 * `application/octet-stream` in its place. Announcing THAT is a way to fail —
 * it is not among the types the provider accepts, whereas `audio/mpeg` (what
 * every upload was labelled as before the stored type was read back at all)
 * is, and the container is recognised from the bytes regardless.
 *
 * A CRM or telephony download carries the recording's real header, and the
 * provider reads far more than the six formats the dropzone offers — AAC,
 * AIFF, Opus, AMR, 3GPP, video containers. Narrowing those to `audio/mpeg`
 * would mislabel integration audio that already arrived correctly described
 * (Codex P2 on #826); `audio-storage.ts` keeps extensions for exactly those
 * types, so they demonstrably reach this path.
 *
 * So the question is not "is it one of ours" but "does it name a medium at
 * all": any `audio/*` or `video/*` passes through untouched, and the fallback
 * is only for a type that is missing or cannot be audio.
 */
export function asrAudioMimeType(storedContentType: string | null | undefined): string {
  if (!storedContentType) return 'audio/mpeg';
  const normalized = normalizeMimeType(storedContentType);
  if (normalized.startsWith('audio/') || normalized.startsWith('video/')) {
    return normalized;
  }
  return 'audio/mpeg';
}

/**
 * The extension that belongs with a MIME type, for naming an upload part.
 *
 * The ASR request carries the audio as a multipart file, and that part needs a
 * name; ours said `audio.mp3` for every upload, whatever the bytes were. A type
 * outside the contract has no name here — the caller keeps `.mp3`, which is
 * what it always sent.
 */
export function audioExtensionForMimeType(mimeType: string): string | null {
  const normalized = normalizeMimeType(mimeType);
  const match = ACCEPTED_AUDIO_FORMATS.find((format) =>
    (format.mimeTypes as readonly string[]).includes(normalized)
  );
  return match ? match.extension : null;
}

/**
 * Schema for formData fields in POST /upload
 */
export const uploadFormDataSchema = z
  .object({
    projectId: z.string().uuid('Project ID must be a valid UUID'),
    installationId: z.string().uuid().optional().nullable(),
    type: recordTypeSchema,
    externalId: z.string().optional().nullable(),
    participantName: z.string().optional().nullable(),
    agentId: z.string().uuid().optional().nullable(),
    agentName: z.string().optional().nullable(),
    activityName: z.string().optional().nullable(),
  })
  .strict();

/**
 * Schema for cost estimation request
 */
export const estimateCostRequestSchema = z
  .object({
    projectId: z.string().uuid('Project ID must be a valid UUID'),
    fileSize: z.number().int().positive('File size must be a positive integer'),
    channel: recordTypeSchema,
    diarization: z.boolean().optional().default(false),
  })
  .strict();

// ============================================================================
// Record Response Schemas
// ============================================================================

export const recordResponseSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string(),
  orgId: z.string(),
  projectName: z.string().nullable(),
  installationId: z.string().nullable(),
  type: recordTypeSchema,
  status: recordStatusSchema,
  participantName: z.string().nullable(),
  externalId: z.string().nullable(),
  audioUrl: z.string().nullable(),
  durationSeconds: z.number().nullable(),
  fileSizeBytes: z.number().nullable(),
  errorMessage: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  archivedAt: z.string().nullable(),
  archived: z.boolean().nullable().optional(),
  analysisId: z.string().nullable().optional(),
  /** True if this record was created as demo/sample data. Defaults to false. */
  isDemo: z.boolean().default(false),
});

export const getRecordResponseSchema = z.object({
  record: recordResponseSchema,
});

export const createRecordResponseSchema = z.object({
  record: recordResponseSchema,
  message: z.string().optional(),
});

// ============================================================================
// Transcript Schemas
// ============================================================================

export const transcriptResponseSchema = z.object({
  id: z.string().uuid().nullable(),
  recordId: z.string().uuid(),
  text: z.string().nullable(),
  language: z.string().nullable(),
  confidence: z.number().nullable(),
  provider: z.string().nullable(),
  model: z.string().nullable(),
  diarization: z.unknown().nullable(),
  wordTimestamps: z.unknown().nullable(),
  createdAt: z.string().nullable(),
  processing: z.boolean().optional(),
  message: z.string().optional(),
});

export const getTranscriptResponseSchema = z.object({
  transcript: transcriptResponseSchema,
});

// ============================================================================
// Analysis Schemas
// ============================================================================

export const analysisResponseSchema = z.object({
  id: z.string().uuid(),
  transcriptId: z.string().uuid(),
  recordId: z.string().uuid(),
  resultJson: z.unknown(),
  summary: z.string().nullable(),
  metrics: z.unknown().nullable(),
  insights: z.unknown().nullable(),
  provider: z.string().nullable(),
  model: z.string().nullable(),
  promptProfileId: z.string().uuid().nullable(),
  tokensIn: z.number().int().nullable(),
  tokensOut: z.number().int().nullable(),
  costCents: z.number().int().nullable(),
  createdAt: z.string().nullable(),
});

export const getAnalysisResponseSchema = z.object({
  analysis: analysisResponseSchema,
});

// ============================================================================
// Full RecordWithDetails Schema (for detailed record views)
// ============================================================================

/**
 * RecordWithDetails - Full record with all fields for detail views
 * Used by GET /api/v1/records/:id
 */
export const recordWithDetailsSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string(),
  installationId: z.string().nullable().optional(),
  type: recordTypeSchema,
  externalId: z.string().nullable().optional(),
  status: recordStatusSchema,
  fileUri: z.string().nullable().optional(),
  durationSeconds: z.number().nullable().optional(),
  /**
   * How long the CONVERSATION lasted — first recognised word to last — as
   * opposed to `durationSeconds`, the length of the recording. The two diverge
   * on dead air nobody spoke through: ringing, an IVR greeting, hold music.
   * Null = not measured (records from before migration 073, or an ASR provider
   * without word timestamps); never read it as "no conversation".
   */
  speechSeconds: z.number().nullable().optional(),
  /**
   * The company's OWN external number this call went through, and the carrier
   * behind it — not the counterparty. Tells two outbound lines apart in a list
   * that otherwise shows the same anonymous agent on every row. Null when the
   * provider did not state a line, or the record predates migration 074.
   */
  lineNumber: z.string().nullable().optional(),
  lineCarrier: z.string().nullable().optional(),
  participantName: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  stateUpdatedAt: z.string().nullable().optional(),
  eventReceivedAt: z.string().nullable().optional(),
  mediaDetectedAt: z.string().nullable().optional(),
  expectedMediaSources: z.string().nullable().optional(),
  mediaMeta: z.string().nullable().optional(),
  textContent: z.string().nullable().optional(),
  contentHash: z.string().nullable().optional(),
  /**
   * Время разговора по данным источника (CRM `START_TIME`).
   *
   * Список отдаёт ту же величину под именем `eventTime` — здесь она приходит
   * под своим исходным именем, и второго поля-синонима в ответе намеренно нет.
   * Экран записи показывает её рядом с `createdAt` (моментом приёма в Aelo):
   * пока была видна только одна из двух отметок и без подписи, задержка
   * приёма читалась как время работы анализа (#359).
   *
   * Формат — с оффсетом портала (`2026-08-21T16:03:54+03:00`), тогда как
   * `createdAt` в UTC. Сравнивать их как строки нельзя.
   */
  versionTs: z.string().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  transcriptPreview: z.string().nullable().optional(),
  agentId: z.string().nullable().optional(),
  agentName: z.string().nullable().optional(),
  archived: z.boolean().nullable().optional(),
  projectName: z.string().nullable().optional(),
  stage: z.string().nullable().optional(),
  stageStatus: z.string().nullable().optional(),
  analysisId: z.string().nullable().optional(),
  // CRM Entity fields
  crmActivityName: z.string().nullable().optional(),
  crmEntityType: crmEntityTypeSchema.nullable().optional(),
  crmEntityId: z.string().nullable().optional(),
  crmEntityName: z.string().nullable().optional(),
  /** See `recordListItemSchema` — resolved from `installationId`, not the org. */
  crmVendor: z.string().nullable().optional(),
  crmDomain: z.string().nullable().optional(),
  // Stage snapshot fields (Bitrix24 deal/lead stage at call time, migration 060)
  /** STAGE_ID at call time. Null until stage capture runs (Phase B). */
  crmStageId: z.string().nullable().optional(),
  /** Bitrix24 CATEGORY_ID (pipeline/воронка). Distinct from callCategory. */
  crmPipelineId: z.string().nullable().optional(),
  /** Coarse-grained stage bucket (discovery/qualification/proposal/...). */
  crmStageSemantic: crmStageSemanticSchema.nullable().optional(),
  /** ISO-8601 timestamp of when the stage snapshot was captured. */
  crmStageCapturedAt: z.string().nullable().optional(),
  /** How the stage snapshot was captured (live/stagehistory/unknown). */
  crmStageSource: crmStageSourceSchema.nullable().optional(),
  /**
   * Where `crmStageSemantic` came from (#1206) — `name` is a guess. A value
   * this build does not know reads as null instead of failing the whole
   * records page; a phase with a null origin was written before #1206 and
   * came from the CRM.
   */
  crmStageSemanticOrigin: crmStageSemanticOriginSchema.nullable().optional().catch(null),
  /**
   * Запись не пошла в анализ по Level 2 фильтру. Мерило зависит от канала:
   * у звонка это длительность короче `minCallDurationSeconds`, у чата —
   * число реплик меньше `minChatMessages` либо отсутствие ответа оператора
   * (`evaluateChatSignificance` в @aica/core). Флаг один на оба канала
   * намеренно: на нём висят пропуск анализа, недоставка в CRM, исключение из
   * серий и баннер на карточке записи — той самой, чей контракт описан здесь.
   */
  isShortCall: z.boolean().nullable().optional(),
  /**
   * The project's short-call threshold, so the detail view can state the real
   * minimum instead of the platform default. Null when the project configures
   * none — the UI then falls back to DEFAULT_MIN_CALL_DURATION_SECONDS.
   * Must stay declared: `z.object` strips undeclared keys, which is exactly how
   * this value used to vanish between edge-api and the short-call banner.
   */
  minCallDurationSeconds: z.number().int().positive().nullable().optional(),
  // Call category (automatic categorization)
  callCategory: callCategorySchema.nullable().optional(),
  // Call direction from CRM (inbound/outbound)
  callDirection: z.enum(['inbound', 'outbound']).nullable().optional(),
  // Call series fields (tracks call sequence within a deal/lead)
  callSeriesId: z.string().nullable().optional(),
  callSeriesNumber: z.number().int().min(1).nullable().optional(),
  callSeriesEntityType: z.enum(['deal', 'lead']).nullable().optional(),
  callSeriesEntityId: z.string().nullable().optional(),
  /** Phone number of the agent who handled the interaction. */
  agentPhone: z.string().nullable().optional(),
  /** Phone number of the participant (client/customer). */
  participantPhone: z.string().nullable().optional(),
  /** Agent's rating of AI analysis accuracy (1–5 stars). Null if not yet rated. */
  agentRating: z.number().int().min(1).max(5).nullable().optional(),
  /** Optional text comment left alongside the star rating */
  agentRatingText: z.string().nullable().optional(),
  /** ISO-8601 timestamp of the last rating action */
  agentRatingAt: z.string().nullable().optional(),
  /** Clerk userId of the person who submitted the rating */
  agentRatingUserId: z.string().nullable().optional(),
  agentRatingUserName: z.string().nullable().optional(),
  /** True if this record was created as demo/sample data. Defaults to false. */
  isDemo: z.boolean().default(false),
  /** Deal outcome from call_series_outcomes (Phase C, C-1). Null when the record isn't a deal series or the outcome hasn't synced yet. */
  dealOutcome: callSeriesOutcomeSemanticSchema.nullable().optional(),
  /** Per-record stage progression vs. the Phase B baseline snapshot, evaluated once by the outcome-sync cron (Phase C, C-3). Null until evaluated. */
  crmStageProgression: crmStageProgressionSchema.nullable().optional(),
  /** ISO-8601 timestamp of when progression was evaluated — this is the render gate for the progression chip (null = not yet evaluated). */
  crmProgressionEvaluatedAt: z.string().nullable().optional(),
  /**
   * Право зрителя на платные действия над этой записью (повтор обработки) —
   * тот же `record.update`, что проверяет их маршрут: клиент и админ по всей
   * организации, супервизор — через членство в проекте (#1264). Нет поля —
   * ответ не пришёл (строка списка), а не «нельзя».
   */
  viewerCanUpdate: z.boolean().optional(),
  /**
   * Что сделает повтор этой записи (`retryOutcomeSchema`) — только у сбойной
   * записи и только в ответе на неё саму. `no_source` — для любого зрителя,
   * остальное — только тому, кто вправе повторить. Нет поля — не сбойная,
   * у зрителя нет права или ответ не пришёл.
   */
  retryOutcome: retryOutcomeSchema.optional(),
});

// ============================================================================
// Record with Details Response Schema
// ============================================================================

/**
 * Response schema for GET /api/v1/records/:id
 * Returns { record: RecordWithDetails }
 */
export const recordWithDetailsResponseSchema = z.object({
  record: recordWithDetailsSchema,
});

// ============================================================================
// Type exports (inferred from schemas)
// ============================================================================

export type RecordType = z.infer<typeof recordTypeSchema>;
export type RecordStatus = z.infer<typeof recordStatusSchema>;
export type CrmEntityType = z.infer<typeof crmEntityTypeSchema>;
export type CrmStageSemantic = z.infer<typeof crmStageSemanticSchema>;
export type CrmStageSource = z.infer<typeof crmStageSourceSchema>;
export type CrmStageSemanticOrigin = z.infer<typeof crmStageSemanticOriginSchema>;
export type CallCategory = z.infer<typeof callCategorySchema>;

export type ListRecordsQuery = z.infer<typeof listRecordsQuerySchema>;
export type CreateRecordRequest = z.infer<typeof createRecordRequestSchema>;
export type UpdateRecordRequest = z.infer<typeof updateRecordRequestSchema>;
export type UploadRecordRequest = z.infer<typeof uploadRecordRequestSchema>;
export type ReplayAuthorization = z.infer<typeof replayAuthorizationSchema>;
export type ReanalyzeRequest = z.infer<typeof reanalyzeRequestSchema>;
export type UploadFormData = z.infer<typeof uploadFormDataSchema>;
export type EstimateCostRequest = z.infer<typeof estimateCostRequestSchema>;

export type RecordResponse = z.infer<typeof recordResponseSchema>;
export type RecordListItem = z.infer<typeof recordListItemSchema>;
export type TranscriptResponse = z.infer<typeof transcriptResponseSchema>;
export type AnalysisResponse = z.infer<typeof analysisResponseSchema>;
export type RecordWithDetails = z.infer<typeof recordWithDetailsSchema>;
